import React, { useEffect, useRef } from 'react';

interface TimelineItemProps {
  title: string;
  organization: string;
  period: string;
  description: string;
  logo?: string;
  location?: string;
  highlights?: string[];
  links?: Array<{
    label: string;
    url: string;
  }>;
  isLast?: boolean;
}

const TimelineItem: React.FC<TimelineItemProps> = ({
  title,
  organization,
  period,
  description,
  logo,
  location,
  highlights,
  links,
  isLast = false,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div className="relative animate-fade-up" ref={itemRef}>
      {!isLast && (
        <div className="absolute top-8 bottom-0 left-4 w-0.5 bg-portfolio-blue/20"></div>
      )}
      
      <div className="flex gap-6">
        <div className="relative z-10 flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-portfolio-blue text-white shadow-md">
          {logo ? (
            <img 
              src={logo} 
              alt={organization} 
              className="w-6 h-6 rounded-full object-cover"
            />
          ) : (
            <span className="text-xs font-bold">
              {organization.charAt(0)}
            </span>
          )}
        </div>
        
        <div className="pb-10">
          <div className="flex flex-wrap justify-between items-start mb-2">
            <div>
              <h3 className="font-semibold text-lg text-portfolio-blue">{title}</h3>
              <div className="text-gray-700 font-medium">{organization}</div>
            </div>
            <div className="text-right">
              <div className="inline-block bg-portfolio-blue/10 text-portfolio-blue text-sm px-3 py-1 rounded-full">
                {period}
              </div>
              {location && (
                <div className="text-sm text-gray-500 mt-1">{location}</div>
              )}
            </div>
          </div>
          
          <p className="text-gray-600 mb-3">{description}</p>
          
          {highlights && highlights.length > 0 && (
            <ul className="list-disc list-inside space-y-1 text-gray-600 mb-3">
              {highlights.map((highlight, index) => (
                <li key={index} className="text-sm">{highlight}</li>
              ))}
            </ul>
          )}
          
          {links && links.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-portfolio-blue hover:underline"
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelineItem;
